import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query"
import {
  getNotificationHistories,
  type NotificationHistoryItem,
  type NotificationHistoriesResponse,
} from "@/services/notification-histories"
import type { AppNotification, NotificationType } from "@/types/notification"
import { runNotificationReceiveBehavior } from "@/lib/notifications/notification-behaviors"
import { decodeHTML } from "entities"

const NOTIFICATION_HISTORIES_PER_PAGE = 10

function normalizeScope(scope?: string | null): string {
  if (!scope || typeof scope !== "string") return "guest"
  return scope.toLowerCase()
}

export const NOTIFICATION_HISTORY_QUERY_KEYS = {
  LIST: (scope?: string | null) =>
    ["notification-histories", normalizeScope(scope)] as const,
}

type NotificationHistoryPage = {
  data: AppNotification[]
  meta: NotificationHistoriesResponse["meta"]
}

function mapTypeToUi(type: string): NotificationType {
  if (type === "expiration") return "warning"
  if (type === "balances_usd") return "error"
  if (type === "balances_percent") return "warning"
  return "info"
}

function decodeHtmlEntities(value?: string | null): string | undefined {
  if (value == null) return undefined
  return decodeHTML(value)
}

function mapHistoryItemToNotification(
  item: NotificationHistoryItem
): AppNotification {
  const createdAt = Date.parse(item.created_at)

  return {
    id: item.id,
    type: mapTypeToUi(item.notification_type),
    title: decodeHtmlEntities(item.title) ?? "",
    description: decodeHtmlEntities(item.body),
    read: true,
    createdAt: Number.isNaN(createdAt) ? Date.now() : createdAt,
    data: {
      ...(item.payload ?? {}),
      type: item.notification_type,
    },
  }
}

export function useNotificationHistories(
  scope?: string | null,
  enabled = true
) {
  const queryResult = useInfiniteQuery({
    queryKey: NOTIFICATION_HISTORY_QUERY_KEYS.LIST(scope),
    enabled,
    initialPageParam: 1,
    staleTime: 30000,
    queryFn: async ({ pageParam = 1 }) => {
      try {
        const response = await getNotificationHistories({
          page: pageParam,
          per: NOTIFICATION_HISTORIES_PER_PAGE,
        })
        return {
          data: response.data.map(mapHistoryItemToNotification),
          meta: response.meta,
        } satisfies NotificationHistoryPage
      } catch {
        return {
          data: [],
          meta: {
            current_page: 1,
            next_page: null,
            prev_page: null,
            total_pages: 0,
            total_count: 0,
          },
        } satisfies NotificationHistoryPage
      }
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.meta) return undefined
      const { current_page, total_pages } = lastPage.meta
      return current_page < total_pages ? current_page + 1 : undefined
    },
  })

  const notifications =
    queryResult.data?.pages?.flatMap((page) => page?.data ?? []) ?? []
  const unreadCount = notifications.filter((item) => !item.read).length
  const latest = notifications[0] ?? undefined
  const totalCount = queryResult.data?.pages?.at(-1)?.meta?.total_count ?? 0

  return {
    ...queryResult,
    notifications,
    unreadCount,
    latest,
    totalCount,
  }
}

export function useNotificationReadActions() {
  const queryClient = useQueryClient()

  const markRead = useMutation({
    mutationFn: async ({ id }: { id: string; scope?: string | null }) => id,
    onSuccess: (id, variables) => {
      queryClient.setQueryData<InfiniteData<NotificationHistoryPage>>(
        NOTIFICATION_HISTORY_QUERY_KEYS.LIST(variables.scope),
        (old) => {
          if (!old?.pages) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((item) =>
                item.id === id ? { ...item, read: true } : item
              ),
            })),
          }
        }
      )
    },
  })

  const markAllRead = useMutation({
    mutationFn: async ({ scope }: { scope?: string | null }) => scope,
    onSuccess: (scope) => {
      queryClient.setQueryData<InfiniteData<NotificationHistoryPage>>(
        NOTIFICATION_HISTORY_QUERY_KEYS.LIST(scope),
        (old) => {
          if (!old?.pages) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((item) => ({ ...item, read: true })),
            })),
          }
        }
      )
    },
  })

  return {
    markRead: (id: string, scope?: string | null) =>
      markRead.mutate({ id, scope }),
    markAllRead: (scope?: string | null) => markAllRead.mutate({ scope }),
  }
}

export function useInjectNotificationHistoryItem() {
  const queryClient = useQueryClient()

  return ({
    payload,
    scope,
  }: {
    payload: {
      type?: NotificationType | string
      title: string
      description?: string
      data?: Record<string, unknown>
      rawPayload?: string
    }
    scope?: string | null
  }) => {
    const item: AppNotification = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      type: payload.type ?? "info",
      title: payload.title,
      description: payload.description,
      read: false,
      createdAt: Date.now(),
      data: payload.data,
      rawPayload: payload.rawPayload,
    }

    runNotificationReceiveBehavior({ item, queryClient })

    queryClient.setQueryData<InfiniteData<NotificationHistoryPage>>(
      NOTIFICATION_HISTORY_QUERY_KEYS.LIST(scope),
      (old) => {
        if (!old?.pages || old.pages.length === 0) {
          return {
            pageParams: [1],
            pages: [
              {
                data: [item],
                meta: {
                  current_page: 1,
                  next_page: null,
                  prev_page: null,
                  total_pages: 1,
                  total_count: 1,
                },
              },
            ],
          }
        }

        const [firstPage, ...rest] = old.pages
        return {
          ...old,
          pages: [
            {
              ...firstPage,
              data: [item, ...firstPage.data],
              meta: {
                ...firstPage.meta,
                total_count: (firstPage.meta?.total_count ?? 0) + 1,
              },
            },
            ...rest,
          ],
        }
      }
    )
  }
}
